import { useEffect, useState } from "react";
import type { TrackedOrder } from "../../api/types";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { formatTime } from "../../lib/time";

/** How often the courier moves on the map (prototype: every second). */
const TICK_MS = 1_000;

/**
 * How far the courier is between the shop and the customer: by the time since he left and his
 * expected arrival (prototype openTrack), between 3% and 97% while on the way, 100% once delivered.
 */
export function rideProgress(order: Pick<TrackedOrder, "departedAt" | "eta" | "deliveredAt">, now: number): number {
  if (order.deliveredAt !== null) return 1;
  if (order.departedAt === null || order.eta === null) return 0;
  const left = new Date(order.departedAt).getTime();
  const due = new Date(order.eta).getTime();
  return Math.min(0.97, Math.max(0.03, (now - left) / Math.max(1, due - left)));
}

type Point = readonly [number, number];

/** The courier's way on the drawn map (prototype trkPath): two curves from the shop to the customer. */
const RIDE: readonly (readonly [Point, Point, Point, Point])[] = [
  [
    [40, 130],
    [100, 140],
    [120, 70],
    [180, 85],
  ],
  // "S 255 50, 285 38": the first control point mirrors the last one of the curve before
  [
    [180, 85],
    [240, 100],
    [255, 50],
    [285, 38],
  ],
];

const bezier = ([a, b, c, d]: readonly [Point, Point, Point, Point], s: number): Point => {
  const r = 1 - s;
  const at = (i: 0 | 1) => r * r * r * a[i] + 3 * r * r * s * b[i] + 3 * r * s * s * c[i] + s * s * s * d[i];
  return [at(0), at(1)];
};

/** Points along the way with the distance travelled up to each, to walk it at an even pace. */
const STEPS: readonly { point: Point; length: number }[] = (() => {
  const steps: { point: Point; length: number }[] = [{ point: RIDE[0]![0], length: 0 }];
  for (const curve of RIDE) {
    for (let step = 1; step <= 50; step++) {
      const point = bezier(curve, step / 50);
      const last = steps.at(-1)!;
      steps.push({ point, length: last.length + Math.hypot(point[0] - last.point[0], point[1] - last.point[1]) });
    }
  }
  return steps;
})();

/** Where the courier is on the drawn way at this part of the ride (0 at the shop, 1 at the door). */
export function pointOnRide(progress: number): Point {
  const target = STEPS.at(-1)!.length * Math.min(1, Math.max(0, progress));
  const index = STEPS.findIndex((step) => step.length >= target);
  if (index <= 0) return STEPS[0]!.point;
  const before = STEPS[index - 1]!;
  const after = STEPS[index]!;
  const part = (target - before.length) / Math.max(1e-9, after.length - before.length);
  return [
    before.point[0] + (after.point[0] - before.point[0]) * part,
    before.point[1] + (after.point[1] - before.point[1]) * part,
  ];
}

/** Minutes until the expected arrival, at least 1 (prototype "nog 12 min"). */
export function minutesLeft(eta: string, now: number): number {
  return Math.max(1, Math.round((new Date(eta).getTime() - now) / 60_000));
}

/**
 * The card of "Volg je bestelling" (prototype openTrack): the shop and the status, the map with the
 * courier moving from the shop to the customer once he left, and the order with what is still to be
 * paid. The customer sees it behind the link in the mail; the till shows the same ("Volgpagina klant").
 */
export function TrackCard({ order }: { order: TrackedOrder }) {
  const text = t.track;
  const delivery = order.channel === "delivery";
  const onTheWay = delivery && order.status !== "cancelled" && order.departedAt !== null;
  const moving = onTheWay && order.deliveredAt === null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!moving) return;
    const timer = setInterval(() => {
      setNow(Date.now());
    }, TICK_MS);
    return () => {
      clearInterval(timer);
    };
  }, [moving]);

  // the courier's real position when his phone tells it; otherwise the estimate from the departure
  const live = moving ? (order.live ?? null) : null;
  const [x, y] = pointOnRide(live === null ? rideProgress(order, now) : live.progress);
  const eta = live?.eta ?? order.eta;

  const ready = formatTime(order.readyAt);
  const status =
    order.status === "cancelled" ? (
      <span className="stp off">{text.cancelled}</span>
    ) : delivery && order.deliveredAt !== null ? (
      <span className="stp on">{text.delivered}</span>
    ) : onTheWay && eta !== null ? (
      <span>
        {live !== null && <span className="trk-live">{text.live} </span>}
        {text.arrival[0]}
        <b>{formatTime(eta)}</b>
        {text.arrival[1](minutesLeft(eta, now))}
      </span>
    ) : delivery ? (
      <span>
        {text.preparing} · {text.readyDelivery(ready)}
      </span>
    ) : (
      <span>{text.readyTakeaway(ready)}</span>
    );

  return (
    <div className="trackcard">
      <div className="trk-top">
        <b>{order.shop.name}</b>
        {status}
      </div>
      {delivery && order.status !== "cancelled" && (
        <svg viewBox="0 0 320 170" className="trkmap" role="img" aria-label={onTheWay ? text.map : text.title}>
          <rect x="0" y="0" width="320" height="170" rx="10" className="trk-bg" />
          <path d="M20 140 C 90 150, 110 60, 180 80 S 270 40, 300 30" className="trk-road" />
          <circle cx="40" cy="130" r="9" className="trk-shop" />
          <text x="40" y="158" textAnchor="middle" className="trk-lbl">
            {text.shop}
          </text>
          <circle cx="285" cy="38" r="9" className="trk-home" />
          <text x="285" y="66" textAnchor="middle" className="trk-lbl">
            {text.home}
          </text>
          {onTheWay && (
            <g className="trk-dot" transform={`translate(${x.toFixed(1)},${y.toFixed(1)})`}>
              <circle r="11" className="trk-cour" />
              <text y="4" textAnchor="middle" className="trk-ic">
                🛵
              </text>
            </g>
          )}
        </svg>
      )}
      <div className="trk-info">
        <span>
          {text.order(order.code)} · {order.items}
        </span>
        <span>{text.total(formatEuro(order.totalCents))}</span>
        {order.address !== null && <span>{order.address}</span>}
        {order.status !== "cancelled" && (
          <span>
            {order.dueCents === 0
              ? text.paid
              : delivery
                ? text.dueDoor(formatEuro(order.dueCents), order.bankQr)
                : text.dueCounter(formatEuro(order.dueCents))}
          </span>
        )}
        {order.shop.phone !== "" && <span className="proto">{text.question(order.shop.phone)}</span>}
        <span className="trk-by">
          {text.by} <img src="/tafo-logo.png" alt="Tafo" />
        </span>
      </div>
    </div>
  );
}
